import { Component, inject, ChangeDetectorRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from '../../../core/services/snackbar.service';

@Component({
    selector: 'fk-snackbar-container',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    template: `
        <div class="fixed bottom-4 right-4 left-4 sm:bottom-8 sm:right-8 sm:left-auto z-[9999] flex flex-col items-end gap-3 pointer-events-none w-auto sm:max-w-[640px]">
            @for (bar of snackbarService.snackbars(); track bar.id) {
                <div class="pointer-events-auto p-4 pr-12 rounded-lg shadow-lg bg-white text-slate-800 border border-slate-100 relative overflow-hidden flex items-center gap-4 transition-all duration-300 animate-in slide-in-from-bottom-4 fade-in w-full sm:w-max sm:min-w-[320px] sm:max-w-[640px]">
                    <!-- Left status icon -->
                    @if (bar.type === 'success') {
                        <img src="assets/icons/sb-success.svg" alt="Success" class="w-10 h-10 shrink-0 select-none" />
                    } @else if (bar.type === 'error') {
                        <img src="assets/icons/sb-failed.svg" alt="Error" class="w-10 h-10 shrink-0 select-none" />
                    } @else {
                        <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-slate-600 text-white select-none">
                            <mat-icon class="text-[20px] w-5 h-5 flex items-center justify-center font-bold">info</mat-icon>
                        </div>
                    }

                    <!-- Title and Message text stack -->
                    <div class="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span class="text-[15px] font-bold text-[#0F172A] leading-tight select-none">
                            {{ bar.title || getDefaultTitle(bar.type) }}
                        </span>
                        <span class="text-[13px] font-medium text-[#64748B] leading-snug select-none break-words">
                            {{ bar.message }}
                        </span>
                    </div>

                    <!-- Right close button -->
                    <button (click)="snackbarService.dismiss(bar.id!)"
                            class="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center transition-opacity hover:opacity-80 cursor-pointer shrink-0">
                        <img src="assets/icons/sb-close.svg" alt="Close" class="w-6 h-6" />
                    </button>

                    <!-- Animated bottom progress bar -->
                    @if (bar.duration && bar.duration > 0) {
                        <div class="absolute bottom-0 left-0 right-0 h-[3.5px] bg-slate-50">
                            <div class="h-full progress-bar-shrink"
                                 [ngClass]="{
                                     'bg-[#06BE41]': bar.type === 'success',
                                     'bg-[#FF6767]': bar.type === 'error',
                                     'bg-slate-500': bar.type === 'info'
                                 }"
                                 [style.animation-duration.ms]="bar.duration">
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: [`
        @keyframes progress-shrink {
            from { width: 100%; }
            to { width: 0%; }
        }
        .progress-bar-shrink {
            animation-name: progress-shrink;
            animation-timing-function: linear;
            animation-fill-mode: forwards;
        }
    `]
})
export class SnackbarContainerComponent {
    snackbarService = inject(SnackbarService);
    private cdr = inject(ChangeDetectorRef);

    constructor() {
        effect(() => {
            this.snackbarService.snackbars();
            this.cdr.markForCheck();
        });
    }

    getDefaultTitle(type: 'success' | 'info' | 'error'): string {
        switch (type) {
            case 'success':
                return 'Success';
            case 'error':
                return 'Error';
            case 'info':
                return 'Info';
            default:
                return '';
        }
    }
}
